import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { describeError } from '../../lib/api.ts';
import { api } from '../../lib/client.ts';
import { useSession } from '../../lib/session.tsx';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select.tsx';

const LABEL = { email: 'Email', phone: 'Phone' } as const;
const MODE = {
  block: 'Block — never create a duplicate',
  warn: 'Warn — ask, then allow',
  off: 'Off — no check',
} as const;

/**
 * Duplicate rules (RELAY-947) in Settings: per field, block / warn / off. Everyone sees what
 * is in force; only an organisation admin can change it.
 */
export function DuplicateRules() {
  const qc = useQueryClient();
  const session = useSession();
  const canManage = session.can('organisation.manage');
  const rules = useQuery({ queryKey: ['dedupe-rules'], queryFn: () => api.dedupeRules.list() });
  const [error, setError] = useState<string | null>(null);
  const set = useMutation({
    mutationFn: (v: { field: 'email' | 'phone'; mode: 'off' | 'warn' | 'block' }) =>
      api.dedupeRules.set(v.field, v.mode),
    onSuccess: (next) => {
      setError(null);
      qc.setQueryData(['dedupe-rules'], next);
    },
    onError: (err) => setError(describeError(err)),
  });
  return (
    <section className="flex flex-col gap-3" data-testid="duplicate-rules">
      <div>
        <h3 className="text-base font-semibold tracking-tight">Duplicate candidates</h3>
        <p className="text-muted-foreground text-sm">
          What happens when a new or edited candidate has the email or phone of an existing one. For
          the whole organization.
        </p>
      </div>
      {rules.data?.map((r) => (
        <label
          key={r.field}
          className="flex flex-wrap items-center justify-between gap-x-3 gap-y-1.5 text-sm font-medium"
        >
          <span>{LABEL[r.field]}</span>
          <Select
            value={r.mode}
            disabled={!canManage}
            onValueChange={(mode) =>
              set.mutate({ field: r.field, mode: mode as 'off' | 'warn' | 'block' })
            }
          >
            <SelectTrigger className="w-full sm:w-72" aria-label={`${LABEL[r.field]} duplicates`}>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {(Object.keys(MODE) as Array<keyof typeof MODE>).map((m) => (
                <SelectItem key={m} value={m}>
                  {MODE[m]}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </label>
      ))}
      {error && (
        <p className="text-danger text-sm" role="alert">
          {error}
        </p>
      )}
    </section>
  );
}
